import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Grid,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as TestIcon,
  Link as LinkIcon,
  Security as SecurityIcon,
  Api as ApiIcon,
  Http as HttpIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { connectorsApi } from '../../api/connectors';
import { StatusBadge } from '../../components/common/StatusBadge';
import { useToast } from '../../hooks/useToast';
import { EmptyState } from '../../components/common/EmptyState';
import { PREDEFINED_ENDPOINTS } from '../../utils/staticvariables';

const K = {
  navy: '#121826',
  blue: '#4185F1',
  border: 'rgba(18, 24, 38, 0.08)',
  surface: '#F8FAFC',
};
const kobraPageSx = { p: 3, width: '100%', maxWidth: { sm: '100%', md: '1700px' }, bgcolor: K.surface };
const kobraFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5,
    backgroundColor: '#fff',
    '& fieldset': { borderColor: 'rgba(18, 24, 38, 0.1)' },
    '&:hover fieldset': { borderColor: `${K.blue}55` },
    '&.Mui-focused fieldset': { borderColor: K.blue, borderWidth: 2 },
  },
};
const kobraSecondaryBtnSx = {
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 1.5,
  borderColor: K.border,
  color: K.navy,
};
const kobraPrimaryBtnSx = {
  '&&': {
    borderRadius: 1.5,
    px: 3,
    py: 1.25,
    textTransform: 'none',
    fontWeight: 700,
    bgcolor: K.blue,
    color: '#fff',
    border: 'none',
    backgroundImage: 'none',
    boxShadow: `0 4px 14px ${alpha(K.blue, 0.35)}`,
    '&:hover': {
      bgcolor: '#3574d9',
      border: 'none',
      backgroundImage: 'none',
      boxShadow: `0 6px 18px ${alpha(K.blue, 0.38)}`,
    },
  },
};
const kobraCardSx = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 2,
  border: `1px solid ${K.border}`,
  boxShadow: '0 1px 4px rgba(18, 24, 38, 0.06)',
  bgcolor: '#fff',
  transition: 'all .2s ease',
  '&:hover': { boxShadow: '0 6px 18px rgba(18, 24, 38, 0.12)', transform: 'translateY(-2px)' },
};

export const Connectors = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [editingConnector, setEditingConnector] = useState(null);
  const [testingConnector, setTestingConnector] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    baseUrl: '',
    authType: 'bearer',
    bearerToken: '',
    apiKey: '',
    apiKeyHeader: 'X-API-Key',
    username: '',
    password: '',
    endpoints: [],
  });
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const { data: connectors, isLoading } = useQuery({
    queryKey: ['connectors'],
    queryFn: () => connectorsApi.getConnectors(),
  });

  const { data: testResult, isLoading: testLoading } = useQuery({
    queryKey: ['testConnector', testingConnector],
    queryFn: () => connectorsApi.testConnector(testingConnector),
    enabled: !!testingConnector && testDialogOpen,
  });

  // Initialize endpoints from predefined list
  const initializeEndpoints = (existingEndpoints = []) => {
    return PREDEFINED_ENDPOINTS.map((predefined) => {
      const existing = existingEndpoints.find((ep) => ep.name === predefined.name);
      return {
        name: predefined.name,
        method: predefined.method,
        path: existing?.path || predefined.defaultPath,
        enabled: existing ? (existing.enabled !== false) : false,
      };
    });
  };

  const handleCreate = () => {
    setEditingConnector(null);
    setFormData({
      name: '',
      baseUrl: '',
      authType: 'bearer',
      bearerToken: '',
      apiKey: '',
      apiKeyHeader: 'X-API-Key',
      username: '',
      password: '',
      endpoints: initializeEndpoints(),
    });
    setDialogOpen(true);
  };

  const handleEdit = (connector) => {
    setEditingConnector(connector);
    
    // Extract auth config
    const authConfig = connector.authConfig || {};
    const endpoints = initializeEndpoints(connector.endpoints || []);
    
    setFormData({
      name: connector.name || '',
      baseUrl: connector.baseUrl || '',
      authType: connector.authType || 'bearer',
      bearerToken: authConfig.token || '',
      apiKey: authConfig.key || '',
      apiKeyHeader: authConfig.header || 'X-API-Key',
      username: authConfig.username || '',
      password: authConfig.password || '',
      endpoints,
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingConnector(null);
  };

  const handleTest = (id) => {
    setTestingConnector(id);
    setTestDialogOpen(true);
  };

  const handleUpdateEndpoint = (endpointName, field, value) => {
    const newEndpoints = formData.endpoints.map((ep) =>
      ep.name === endpointName ? { ...ep, [field]: value } : ep
    );
    setFormData({ ...formData, endpoints: newEndpoints });
  };

  const createMutation = useMutation({
    mutationFn: (data) => connectorsApi.createConnector(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connectors'] });
      setDialogOpen(false);
      showToast('Conector creado exitosamente', 'success');
    },
    onError: (error) => {
      showToast(error.message || 'Error al crear conector', 'error');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => connectorsApi.updateConnector(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connectors'] });
      setDialogOpen(false);
      showToast('Conector actualizado exitosamente', 'success');
    },
    onError: (error) => {
      showToast(error.message || 'Error al actualizar conector', 'error');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => connectorsApi.deleteConnector(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connectors'] });
      showToast('Conector eliminado exitosamente', 'success');
    },
    onError: (error) => {
      showToast(error.message || 'Error al eliminar conector', 'error');
    },
  });

  const handleSave = () => {
    // Build auth config based on auth type
    let authConfig = {};
    if (formData.authType === 'bearer' && formData.bearerToken) {
      authConfig = { token: formData.bearerToken };
    } else if (formData.authType === 'api_key' && formData.apiKey) {
      authConfig = { key: formData.apiKey, header: formData.apiKeyHeader };
    } else if (formData.authType === 'basic' && formData.username && formData.password) {
      authConfig = { username: formData.username, password: formData.password };
    }

    // Filter only enabled endpoints and validate
    const enabledEndpoints = formData.endpoints
      .filter((ep) => ep.enabled)
      .map((ep) => {
        const predefined = PREDEFINED_ENDPOINTS.find((p) => p.name === ep.name);
        if (!predefined) {
          throw new Error(`Endpoint ${ep.name} no es un endpoint predefinido válido`);
        }
        if (ep.method !== predefined.method) {
          throw new Error(`El método HTTP para ${ep.name} debe ser ${predefined.method}`);
        }
        if (!ep.path) {
          throw new Error(`La ruta para ${ep.name} es requerida`);
        }
        return {
          name: ep.name,
          method: ep.method,
          path: ep.path,
          responseMapping: {},
        };
      });

    const dataToSave = {
      name: formData.name,
      baseUrl: formData.baseUrl,
      authType: formData.authType,
      ...(formData.authType === 'bearer' && { bearerToken: formData.bearerToken }),
      ...(formData.authType === 'api_key' && { apiKey: formData.apiKey, apiKeyHeader: formData.apiKeyHeader }),
      ...(formData.authType === 'basic' && { username: formData.username, password: formData.password }),
      endpoints: enabledEndpoints,
    };

    if (editingConnector) {
      updateMutation.mutate({ id: editingConnector.id, data: dataToSave });
    } else {
      createMutation.mutate(dataToSave);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este conector?')) {
      deleteMutation.mutate(id);
    }
  };

  const getMethodColor = (method) => {
    const colors = {
      GET: 'primary',
      POST: 'success',
      PUT: 'warning',
      PATCH: 'info',
      DELETE: 'error',
    };
    return colors[method] || 'default';
  };

  const getAuthTypeLabel = (type) => {
    const labels = {
      bearer: 'Bearer Token',
      api_key: 'API Key',
      basic: 'Basic Auth',
      none: 'Sin autenticación',
    };
    return labels[type] || type;
  };

  if (isLoading) {
    return (
      <Box sx={{ ...kobraPageSx, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <Typography sx={{ color: alpha(K.navy, 0.55) }}>Cargando conectores…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={kobraPageSx}>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 1.5,
              bgcolor: alpha(K.blue, 0.12),
              display: 'flex',
            }}
          >
            <LinkIcon sx={{ fontSize: 32, color: K.blue }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: K.navy, letterSpacing: '-0.02em' }}>
              Conectores
            </Typography>
            <Typography variant="body2" sx={{ color: alpha(K.navy, 0.55), maxWidth: 520 }}>
              Gestiona tus integraciones con servicios externos
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={kobraPrimaryBtnSx}
        >
          Crear Conector
        </Button>
      </Box>

      {/* Connectors Grid */}
      {!connectors?.length ? (
        <Card sx={{ ...kobraCardSx, borderRadius: 2 }}>
          <CardContent sx={{ py: 2 }}>
            <EmptyState
              icon={LinkIcon}
              title="No hay Conectores"
              description="Crea tu primer conector para integrar con servicios externos"
              actionLabel="Crear Conector"
              onAction={handleCreate}
            />
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {connectors.map((connector) => {
            const enabledEndpoints = (connector.endpoints || []).filter((ep) => ep.enabled !== false);
            return (
              <Grid item xs={12} sm={6} md={4} key={connector.id}>
                <Card sx={kobraCardSx}>
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            backgroundColor: alpha(K.blue, 0.12),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <LinkIcon sx={{ fontSize: 28, color: K.blue }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: K.navy }} noWrap>
                            {connector.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              wordBreak: 'break-all',
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {connector.baseUrl}
                          </Typography>
                        </Box>
                      </Box>
                      <StatusBadge status={connector.status} />
                    </Box>

                    {/* Info Section */}
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <SecurityIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Autenticación:
                        </Typography>
                        <Chip
                          label={getAuthTypeLabel(connector.authType)}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            backgroundColor: 'action.selected',
                          }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ApiIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Endpoints:
                        </Typography>
                        <Chip
                          label={enabledEndpoints.length}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            bgcolor: alpha(K.blue, 0.12),
                            color: K.blue,
                            border: `1px solid ${alpha(K.blue, 0.22)}`,
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Endpoints Preview */}
                    {enabledEndpoints.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                          Endpoints configurados:
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {enabledEndpoints.slice(0, 3).map((endpoint, idx) => {
                            const predefined = PREDEFINED_ENDPOINTS.find((p) => p.name === endpoint.name);
                            return (
                              <Box key={endpoint.id || idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                  icon={<HttpIcon sx={{ fontSize: 14 }} />}
                                  label={`${endpoint.method} ${predefined?.label || endpoint.name}`}
                                  size="small"
                                  color={getMethodColor(endpoint.method)}
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              </Box>
                            );
                          })}
                          {enabledEndpoints.length > 3 && (
                            <Typography variant="caption" color="text.secondary">
                              +{enabledEndpoints.length - 3} más
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}

                    <Divider sx={{ my: 2, borderColor: K.border }} />

                    {/* Actions */}
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Tooltip title="Probar">
                        <IconButton
                          size="small"
                          onClick={() => handleTest(connector.id)}
                          sx={{
                            color: 'success.main',
                            '&:hover': {
                              backgroundColor: 'success.light',
                            },
                          }}
                        >
                          <TestIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(connector)}
                          sx={{
                            color: K.blue,
                            '&:hover': {
                              backgroundColor: alpha(K.blue, 0.1),
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(connector.id)}
                          sx={{
                            color: 'error.main',
                            '&:hover': {
                              backgroundColor: 'error.light',
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${K.border}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            borderBottom: `1px solid ${K.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              p: 1,
              borderRadius: 1.5,
              bgcolor: alpha(K.blue, 0.12),
              display: 'flex',
            }}
          >
            <LinkIcon sx={{ fontSize: 20, color: K.blue }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: K.navy }}>
            {editingConnector ? 'Editar Conector' : 'Crear Conector'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, bgcolor: '#fff' }}>
          {/* Basic Info */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
              Nombre *
            </Typography>
            <TextField
              fullWidth
              placeholder="Nombre del conector"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              sx={{ mb: 2, ...kobraFieldSx }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
              URL Base *
            </Typography>
            <TextField
              fullWidth
              placeholder="https://api.ejemplo.com"
              value={formData.baseUrl}
              onChange={(e) => setFormData({ ...formData, baseUrl: e.target.value })}
              sx={{ mb: 2, ...kobraFieldSx }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
              Tipo de Autenticación
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Select
                value={formData.authType}
                onChange={(e) => setFormData({ ...formData, authType: e.target.value })}
                sx={kobraFieldSx}
              >
                <MenuItem value="bearer">Bearer Token</MenuItem>
                <MenuItem value="api_key">API Key</MenuItem>
                <MenuItem value="basic">Basic Auth</MenuItem>
                <MenuItem value="none">Ninguna</MenuItem>
              </Select>
            </FormControl>

            {/* Auth Config Fields */}
            {formData.authType === 'bearer' && (
              <TextField
                fullWidth
                type="password"
                placeholder="Token de autenticación"
                value={formData.bearerToken}
                onChange={(e) => setFormData({ ...formData, bearerToken: e.target.value })}
                sx={{ mb: 2, ...kobraFieldSx }}
              />
            )}

            {formData.authType === 'api_key' && (
              <>
                <TextField
                  fullWidth
                  placeholder="API Key"
                  value={formData.apiKey}
                  onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                  sx={{ mb: 2, ...kobraFieldSx }}
                />
                <TextField
                  fullWidth
                  placeholder="Header name (ej: X-API-Key)"
                  value={formData.apiKeyHeader}
                  onChange={(e) => setFormData({ ...formData, apiKeyHeader: e.target.value })}
                  sx={{ mb: 2, ...kobraFieldSx }}
                />
              </>
            )}

            {formData.authType === 'basic' && (
              <>
                <TextField
                  fullWidth
                  placeholder="Usuario"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  sx={{ mb: 2, ...kobraFieldSx }}
                />
                <TextField
                  fullWidth
                  type="password"
                  placeholder="Contraseña"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  sx={{ mb: 2, ...kobraFieldSx }}
                />
              </>
            )}
          </Box>

          <Divider sx={{ my: 3, borderColor: K.border }} />

          {/* Predefined Endpoints Configuration */}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: K.navy }}>
              Endpoints Predefinidos
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configura las rutas para los endpoints predefinidos. Solo los endpoints habilitados se guardarán.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {formData.endpoints.map((endpoint) => {
                const predefined = PREDEFINED_ENDPOINTS.find((p) => p.name === endpoint.name);
                if (!predefined) return null;

                return (
                  <Card
                    key={endpoint.name}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${endpoint.enabled ? alpha(K.blue, 0.35) : K.border}`,
                      bgcolor: endpoint.enabled ? alpha(K.blue, 0.06) : '#fff',
                      boxShadow: '0 1px 4px rgba(18, 24, 38, 0.04)',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: K.navy }}>
                            {predefined.label}
                          </Typography>
                          <Chip
                            label={endpoint.method}
                            size="small"
                            color={getMethodColor(endpoint.method)}
                            sx={{ fontWeight: 600 }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {predefined.description}
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={endpoint.enabled}
                            onChange={(e) => handleUpdateEndpoint(endpoint.name, 'enabled', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: K.blue },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                bgcolor: alpha(K.blue, 0.45),
                              },
                            }}
                          />
                        }
                        label={endpoint.enabled ? 'Habilitado' : 'Deshabilitado'}
                        sx={{ ml: 2 }}
                      />
                    </Box>

                    {endpoint.enabled && (
                      <TextField
                        fullWidth
                        size="small"
                        label="Ruta del endpoint"
                        placeholder={predefined.defaultPath}
                        value={endpoint.path}
                        onChange={(e) => handleUpdateEndpoint(endpoint.name, 'path', e.target.value)}
                        helperText={`Ruta completa: ${formData.baseUrl || 'https://api.ejemplo.com'}${endpoint.path || predefined.defaultPath}`}
                        sx={{
                          ...kobraFieldSx,
                          '& .MuiOutlinedInput-root': {
                            ...kobraFieldSx['& .MuiOutlinedInput-root'],
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                          },
                        }}
                      />
                    )}
                  </Card>
                );
              })}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${K.border}`, pt: 2, px: 3, pb: 2, bgcolor: '#fff' }}>
          <Button variant="outlined" onClick={handleCloseDialog} sx={kobraSecondaryBtnSx}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={createMutation.isPending || updateMutation.isPending || !formData.name || !formData.baseUrl}
            sx={{
              ...kobraPrimaryBtnSx,
            }}
          >
            {createMutation.isPending || updateMutation.isPending ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Dialog */}
      <Dialog
        open={testDialogOpen}
        onClose={() => setTestDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, border: `1px solid ${K.border}` } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderBottom: `1px solid ${K.border}`,
            pb: 2,
          }}
        >
          <Box
            sx={{
              p: 0.75,
              borderRadius: 1.5,
              bgcolor: alpha('#2E7D32', 0.12),
              display: 'flex',
            }}
          >
            <TestIcon sx={{ color: '#2E7D32' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: K.navy }}>
            Probar Conector
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, bgcolor: '#fff' }}>
          {testLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">Probando conector...</Typography>
            </Box>
          ) : testResult ? (
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                Solicitud
              </Typography>
              <Box
                sx={{
                  p: 2,
                  mb: 3,
                  bgcolor: K.surface,
                  borderRadius: 1.5,
                  border: `1px solid ${K.border}`,
                }}
              >
                <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                  {JSON.stringify(testResult.request, null, 2)}
                </pre>
              </Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                Respuesta
              </Typography>
              <Box sx={{ p: 2, bgcolor: K.surface, borderRadius: 1.5, border: `1px solid ${K.border}` }}>
                <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                  {JSON.stringify(testResult.response, null, 2)}
                </pre>
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No hay resultados de prueba disponibles
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${K.border}`, pt: 2, px: 3, pb: 2, bgcolor: '#fff' }}>
          <Button variant="outlined" onClick={() => setTestDialogOpen(false)} sx={kobraSecondaryBtnSx}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
